import type { BigOrderRecord, BigOrderRecordList } from '@/models/interface/bigOrder';
import type {
  FunctionalityList,
  GroupList,
  Pagination,
  PaymentInfo,
  PlanInfo,
  PurchaseItem,
  RuleInfo,
  SharingInformation
} from '@/models/interface/profile';
import { ApiFactory, type ApiDataResponse } from '@/utils/api';

export class NewProductService extends ApiFactory {
  constructor() {
    super('NewProduct');
  }

  /**
   * 取得加密過的分享用金鑰
   * @param packageId
   * @returns
   */
  sharingKey(packageId: number): Promise<ApiDataResponse<string>> {
    return this.get(`SharingKey?packageId=${packageId}`);
  }

  /**
   * 點入共享連結後，顯示共享資訊
   * @param key
   * @returns
   */
  sharingInformation(key: string): Promise<ApiDataResponse<SharingInformation>> {
    return this.get(`SharingInformation?key=${key}`);
  }

  /**
   * 解碼傳入的金鑰並綁定方案與被分享人
   * @param key
   * @returns
   */
  bindingMember(key: string): Promise<ApiDataResponse<number>> {
    return this.post(`BindingPackageFunctionalitiesToMember`, { key: key });
  }

  /**
   * 移除綁定
   * @param sharedMemberId
   * @returns
   */
  removeBinding(sharedMemberId: number): Promise<ApiDataResponse<number>> {
    return this.deleteData(`RemovePackageBinding?sharedMemberId=${sharedMemberId}`);
  }

  /**
   * 取得產品分類列表
   * @returns
   */
  sellableProductGroupList(): Promise<ApiDataResponse<GroupList[]>> {
    return this.get(`SellableProductGroupList`);
  }

  /**
   * 依產品類別取得金鑰
   * @param productGroupId
   * @returns
   */
  purchaseKey(productGroupId: number): Promise<ApiDataResponse<string>> {
    return this.get(`PurchaseKey?productGroupId=${productGroupId}`);
  }

  /**
   * 取得產品列表
   * @param memberId
   * @param key
   * @returns
   */
  purchaseItemList(memberId: number, key: string | null): Promise<ApiDataResponse<PurchaseItem>> {
    return this.get(
      `PurchaseItemPage?${memberId === 0 ? '' : `memberId=${memberId}&`}key=${key ? encodeURIComponent(key) : ''}`
    );
  }

  /**
   * 取得產品內容列表
   * @param data
   * @returns
   */
  functionalityList(
    data: Pick<Pagination, 'page' | 'size'>
  ): Promise<ApiDataResponse<FunctionalityList>> {
    return this.post(`FunctionalityList`, data);
  }

  /**
   * 獎金辦法金額(棄用)
   * @param productGroupId
   * @returns
   */
  planInfoList(productGroupId: number): Promise<ApiDataResponse<PlanInfo>> {
    return this.get(`ProfitSharingRuleExample?productGroupId=${productGroupId}`);
  }

  /**
   * 付款頁取得商品資訊
   * @param productId
   * @param purchaseKey
   * @returns
   */
  productPaymentInformation(
    productId: number,
    purchaseKey: string | null
  ): Promise<ApiDataResponse<PaymentInfo>> {
    return this.get(
      `ProductPaymentInformation?productId=${productId}&purchaseKey=${purchaseKey ? encodeURIComponent(purchaseKey) : ''}`
    );
  }

  /**
   * 獎金辦法金額
   * @returns
   */
  ruleList(): Promise<ApiDataResponse<RuleInfo>> {
    return this.get(`ProfitSharingRuleList`);
  }

  recordList(page: number = 1, size: number = 10): Promise<ApiDataResponse<BigOrderRecordList>> {
    return this.get(`RecordList?page=${page}&size=${size}`);
  }
}
